/**
 * Unassign Quiz Modal
 * Allows instructor to select specific assignments to unassign
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiTrash2, FiChevronDown, FiChevronRight, FiCalendar, FiClock, FiCheckCircle, FiAlertCircle, FiBook, FiFileText, FiAward, FiSearch } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';
import ConfirmationModal from './ConfirmationModal';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-color);
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: var(--transition);

  &:hover {
    background: var(--background-tertiary);
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
`;

const QuizInfo = styled.div`
  background: linear-gradient(135deg, rgba(252, 129, 129, 0.05) 0%, rgba(245, 101, 101, 0.02) 100%);
  border: 1px solid var(--error-color);
  border-radius: 0;
  padding: 1rem;
  margin-bottom: 1.5rem;
  opacity: 0.3;
`;

const QuizTitle = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 1rem;
`;

const QuizSubtitle = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 0.75rem;
`;

const QuizMetadata = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid var(--error-color);
  opacity: 0.3;
`;

const QuizMetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  
  svg {
    flex-shrink: 0;
    color: var(--error-color);
  }
`;

const StudentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 500px;
  overflow-y: auto;
`;

const StudentItem = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--background-primary);
  margin-bottom: 0.5rem;
`;

const StudentHeader = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: var(--background-tertiary);
  }

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: var(--error-color);
  }
`;

const StudentInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const StudentName = styled.div`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const StudentEmail = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const AssignmentCount = styled.div`
  font-size: 0.7rem;
  color: var(--error-color);
  font-weight: 500;
  margin-top: 0.25rem;
`;

const StudentAssignmentsList = styled.div`
  padding: 0 0.75rem 0.75rem 2.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const AssignmentItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  cursor: pointer;
  transition: var(--transition);
  background: var(--background-tertiary);

  &:hover {
    background: var(--background-secondary);
    border-color: var(--error-color);
  }

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--error-color);
    margin-top: 0.125rem;
  }
`;

const AssignmentDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.8rem;
`;

const AssignmentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AssignmentId = styled.strong`
  color: var(--text-primary);
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: 0;
  font-size: 0.7rem;
  font-weight: 500;

  &.completed {
    background: rgba(104, 211, 145, 0.1);
    color: var(--success-color);
  }

  &.in_progress {
    background: rgba(246, 224, 94, 0.1);
    color: var(--warning-color);
  }

  &.not_started {
    background: rgba(160, 174, 192, 0.1);
    color: var(--text-muted);
  }
`;

const AssignmentMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  color: var(--text-secondary);
  font-size: 0.75rem;
  align-items: center;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  
  svg {
    flex-shrink: 0;
  }
`;

const ScoreInfo = styled.div`
  font-weight: 500;
  color: var(--text-primary);
`;

const SelectAllContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color);
`;

const SelectAllLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: var(--error-color);
  }
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  background-color: var(--background-primary);
  color: var(--text-primary);
  transition: var(--transition);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  &:focus {
    outline: none;
    border-color: var(--error-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  width: 1rem;
  height: 1rem;
  pointer-events: none;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--error-color);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: 1px solid transparent;
  border-radius: 0;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  &.secondary {
    background: var(--background-primary);
    color: var(--text-secondary);
    border-color: var(--border-color);

    &:hover {
      background: var(--background-tertiary);
      border-color: var(--border-dark);
      color: var(--text-primary);
    }
  }

  &.danger {
    background: linear-gradient(135deg, var(--error-color) 0%, var(--error-dark) 100%);
    color: var(--text-inverse);
    border-color: var(--error-color);
    box-shadow: 0 2px 8px rgba(252, 129, 129, 0.3);

    &:hover {
      background: linear-gradient(135deg, var(--error-dark) 0%, var(--error-color) 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(252, 129, 129, 0.4);
    }

    &:disabled {
      background: var(--error-light);
      border-color: var(--error-light);
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
`;

function UnassignQuizModal({ 
  isOpen, 
  onClose, 
  onConfirm,
  quizId,
  quizTitle 
}) {
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [quizDetails, setQuizDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen && quizId) {
      const loadAssignments = async () => {
        setLoading(true);
        setError(null);
        try {
          // Fetch quiz details
          const quizResponse = await quizApiService.getQuiz(quizId);
          if (quizResponse.success && quizResponse.data) {
            const quiz = quizResponse.data;
            // Calculate question count and total points
            const questionCount = quiz.questions ? quiz.questions.length : 0;
            const totalPoints = quiz.questions ? quiz.questions.reduce((sum, q) => {
              if (q.type === 'matching' && q.settings) {
                try {
                  const settings = typeof q.settings === 'string' ? JSON.parse(q.settings) : q.settings;
                  const leftItems = settings.leftItems || [];
                  const pointsPerTerm = settings.pointsPerTerm || [];
                  return sum + leftItems.reduce((s, item, index) => {
                    if (item && item.trim()) {
                      const termPoints = pointsPerTerm[index] !== undefined ? Number(pointsPerTerm[index]) : 1;
                      return s + termPoints;
                    }
                    return s;
                  }, 0);
                } catch (e) {
                  return sum + (Number(q.points) || 0);
                }
              } else {
                return sum + (Number(q.points) || 0);
              }
            }, 0) : 0;
            
            setQuizDetails({
              ...quiz,
              question_count: questionCount,
              total_points: totalPoints
            });
          }

          const response = await quizApiService.getQuizAttempts(quizId);
          if (response.success && response.data) {
            // Get all assignments with their details
            const assignmentsList = response.data.map(item => ({
              assignment_id: item.assignment_id,
              student_id: item.student_id,
              student_name: item.student_name,
              student_email: item.student_email,
              assigned_at: item.assigned_at,
              due_at: item.due_at,
              attempts_allowed: item.attempts_allowed,
              attempts_used: item.attempts_used,
              time_limit_minutes: item.time_limit_minutes,
              attempt_id: item.attempt_id,
              score: item.score,
              max_score: item.max_score,
              submitted_at: item.submitted_at,
              started_at: item.started_at,
              is_late: item.is_late,
              status: item.submitted_at ? 'completed' : (item.started_at ? 'in_progress' : 'not_started')
            }));

            // Group by student
            const studentsMap = new Map();
            assignmentsList.forEach(assignment => {
              if (!studentsMap.has(assignment.student_id)) {
                studentsMap.set(assignment.student_id, {
                  student_id: assignment.student_id,
                  student_name: assignment.student_name,
                  student_email: assignment.student_email,
                  assignments: []
                });
              }
              studentsMap.get(assignment.student_id).assignments.push(assignment);
            });

            // Convert to array and sort assignments by assigned_at
            const studentsWithAssignments = Array.from(studentsMap.values()).map(student => ({
              ...student,
              assignments: student.assignments.sort((a, b) => 
                new Date(b.assigned_at || 0) - new Date(a.assigned_at || 0)
              )
            }));

            // Auto-expand students with multiple assignments
            studentsWithAssignments.forEach(student => {
              if (student.assignments.length > 1) {
                setExpandedStudents(prev => new Set(prev).add(student.student_id));
              }
            });

            setAssignments(studentsWithAssignments);
          }
        } catch (err) {
          console.error('Error loading assignments:', err);
          setError('Failed to load assignments');
        } finally {
          setLoading(false);
        }
      };
      loadAssignments();
    } else {
      // Reset state when modal closes
      setAssignments([]);
      setSelectedAssignments([]);
      setExpandedStudents(new Set());
      setError(null);
      setQuizDetails(null);
      setSearchTerm('');
    }
  }, [isOpen, quizId]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const filtered = assignments.filter(student => {
        if (!searchTerm.trim()) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
          student.student_name?.toLowerCase().includes(searchLower) ||
          student.student_email?.toLowerCase().includes(searchLower)
        );
      });
      const allAssignmentIds = filtered.flatMap(s => s.assignments.map(a => a.assignment_id));
      setSelectedAssignments(allAssignmentIds);
    } else {
      setSelectedAssignments([]);
    }
  };

  const handleStudentToggle = (studentId) => {
    const student = assignments.find(s => s.student_id === studentId);
    if (!student) return;

    const studentAssignmentIds = student.assignments.map(a => a.assignment_id);
    const allSelected = studentAssignmentIds.every(id => selectedAssignments.includes(id));

    if (allSelected) {
      // Deselect all assignments for this student
      setSelectedAssignments(prev => prev.filter(id => !studentAssignmentIds.includes(id)));
    } else {
      // Select all assignments for this student
      setSelectedAssignments(prev => {
        const newSet = new Set(prev);
        studentAssignmentIds.forEach(id => newSet.add(id));
        return Array.from(newSet);
      });
    }
  };

  const handleAssignmentToggle = (assignmentId) => {
    setSelectedAssignments(prev => {
      if (prev.includes(assignmentId)) {
        return prev.filter(id => id !== assignmentId);
      } else {
        return [...prev, assignmentId];
      }
    });
  };

  const toggleStudentExpanded = (studentId) => {
    setExpandedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const handleConfirm = async () => {
    if (selectedAssignments.length === 0) {
      setShowValidationModal(true);
      return;
    }

    setIsUnassigning(true);
    try {
      await onConfirm(selectedAssignments);
      onClose();
    } catch (err) {
      console.error('Error unassigning quiz:', err);
      setErrorMessage(err.message || 'Failed to unassign quiz. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsUnassigning(false);
    }
  };

  const filteredAssignments = assignments.filter(student => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      student.student_name?.toLowerCase().includes(searchLower) ||
      student.student_email?.toLowerCase().includes(searchLower)
    );
  });

  const allAssignments = filteredAssignments.flatMap(s => s.assignments.map(a => a.assignment_id));
  const allSelected = allAssignments.length > 0 && allAssignments.every(id => selectedAssignments.includes(id));
  const someSelected = selectedAssignments.length > 0 && !allSelected;

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <ModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ModalContent
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle>
              <FiTrash2 size={20} />
              Unassign Quiz
            </ModalTitle>
            <CloseButton onClick={onClose}>
              <FiX size={18} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            <QuizInfo>
              <QuizTitle>{quizTitle || quizDetails?.title || 'Quiz'}</QuizTitle>
              <QuizSubtitle>
                Select specific quiz attempts to unassign. This will permanently delete the attempts and all associated data. This action cannot be undone.
              </QuizSubtitle>
              {quizDetails && (
                <QuizMetadata>
                  {quizDetails.course_name && (
                    <QuizMetaItem>
                      <FiBook size={14} />
                      <span>
                        {quizDetails.course_code ? `${quizDetails.course_code} - ` : ''}
                        {quizDetails.course_name}
                      </span>
                    </QuizMetaItem>
                  )}
                  {quizDetails.question_count !== undefined && quizDetails.question_count !== null && (
                    <QuizMetaItem>
                      <FiFileText size={14} />
                      <span>{quizDetails.question_count} question{quizDetails.question_count !== 1 ? 's' : ''}</span>
                    </QuizMetaItem>
                  )}
                  {quizDetails.total_points !== undefined && quizDetails.total_points !== null && (
                    <QuizMetaItem>
                      <FiAward size={14} />
                      <span>{quizDetails.total_points} point{quizDetails.total_points !== 1 ? 's' : ''}</span>
                    </QuizMetaItem>
                  )}
                </QuizMetadata>
              )}
            </QuizInfo>

            {loading ? (
              <LoadingState>Loading quiz attempts...</LoadingState>
            ) : error ? (
              <ErrorState>{error}</ErrorState>
            ) : assignments.length === 0 ? (
              <EmptyState>No quiz attempts found for this quiz</EmptyState>
            ) : (
              <>
                <SearchContainer>
                  <SearchIcon />
                  <SearchInput
                    type="text"
                    placeholder="Search by student name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </SearchContainer>
                <SelectAllContainer>
                  <SelectAllLabel>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = someSelected;
                      }}
                      onChange={handleSelectAll}
                    />
                    Select All Quiz Attempts
                  </SelectAllLabel>
                </SelectAllContainer>
                <StudentsList>
                  {filteredAssignments.map((student) => {
                    const studentAssignmentIds = student.assignments.map(a => a.assignment_id);
                    const allStudentSelected = studentAssignmentIds.every(id => selectedAssignments.includes(id));
                    const someStudentSelected = studentAssignmentIds.some(id => selectedAssignments.includes(id));
                    const isExpanded = expandedStudents.has(student.student_id);
                    const hasMultiple = student.assignments.length > 1;

                    return (
                      <StudentItem key={student.student_id}>
                        <StudentHeader>
                          <input
                            type="checkbox"
                            checked={allStudentSelected}
                            ref={(input) => {
                              if (input) input.indeterminate = someStudentSelected && !allStudentSelected;
                            }}
                            onChange={() => handleStudentToggle(student.student_id)}
                          />
                          {hasMultiple && (
                            <button
                              type="button"
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--text-secondary)',
                                cursor: 'pointer',
                                padding: '0.25rem',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleStudentExpanded(student.student_id);
                              }}
                            >
                              {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                            </button>
                          )}
                          <StudentInfo>
                            <StudentName>{student.student_name}</StudentName>
                            <StudentEmail>{student.student_email}</StudentEmail>
                            {hasMultiple && (
                              <AssignmentCount>
                                {student.assignments.length} attempt{student.assignments.length > 1 ? 's' : ''}
                              </AssignmentCount>
                            )}
                          </StudentInfo>
                        </StudentHeader>
                        {hasMultiple && isExpanded && (
                          <StudentAssignmentsList>
                            {student.assignments.map((assignment) => (
                              <AssignmentItem key={assignment.assignment_id}>
                                <input
                                  type="checkbox"
                                  checked={selectedAssignments.includes(assignment.assignment_id)}
                                  onChange={() => handleAssignmentToggle(assignment.assignment_id)}
                                />
                                <AssignmentDetails>
                                  <AssignmentHeader>
                                    <AssignmentId>Quiz Attempt # {assignment.assignment_id}</AssignmentId>
                                    <StatusBadge className={assignment.status}>
                                      {assignment.status === 'completed' && <FiCheckCircle size={12} />}
                                      {assignment.status === 'in_progress' && <FiAlertCircle size={12} />}
                                      {assignment.status === 'not_started' && <FiClock size={12} />}
                                      {assignment.status === 'completed' ? 'Completed' : assignment.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                                    </StatusBadge>
                                  </AssignmentHeader>
                                  <AssignmentMeta>
                                    <MetaItem>
                                      <FiCalendar size={14} />
                                      <span>{assignment.due_at ? `Due: ${formatDate(assignment.due_at)}` : 'No due date'}</span>
                                    </MetaItem>
                                    <MetaItem>
                                      <FiClock size={14} />
                                      <span>{assignment.attempts_used || 0}/{assignment.attempts_allowed || 1} attempts</span>
                                    </MetaItem>
                                    {assignment.time_limit_minutes && assignment.time_limit_minutes > 0 ? (
                                      <MetaItem>
                                        <FiClock size={14} />
                                        <span>Timed: {assignment.time_limit_minutes} min</span>
                                      </MetaItem>
                                    ) : (
                                      <MetaItem>
                                        <FiClock size={14} />
                                        <span>No Time Limit</span>
                                      </MetaItem>
                                    )}
                                    {assignment.submitted_at && assignment.score !== null && (
                                      <MetaItem>
                                        <FiAward size={14} />
                                        <ScoreInfo>
                                          Score: {assignment.score}/{assignment.max_score} ({Math.round((assignment.score / assignment.max_score) * 100)}%)
                                        </ScoreInfo>
                                      </MetaItem>
                                    )}
                                    {assignment.assigned_at && (
                                      <MetaItem>
                                        <span>Assigned: {formatDate(assignment.assigned_at)}</span>
                                      </MetaItem>
                                    )}
                                  </AssignmentMeta>
                                </AssignmentDetails>
                              </AssignmentItem>
                            ))}
                          </StudentAssignmentsList>
                        )}
                      </StudentItem>
                    );
                  })}
                </StudentsList>
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Button 
              className="secondary" 
              onClick={onClose}
              disabled={isUnassigning}
            >
              Cancel
            </Button>
            <Button 
              className="danger"
              onClick={handleConfirm}
              disabled={isUnassigning || selectedAssignments.length === 0 || loading}
            >
              <FiTrash2 size={16} />
              {isUnassigning ? 'Unassigning...' : `Unassign ${selectedAssignments.length} Quiz Attempt${selectedAssignments.length !== 1 ? 's' : ''}`}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>

      <ConfirmationModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        onConfirm={() => setShowValidationModal(false)}
        title="No Selection"
        message="Please select at least one quiz attempt to unassign."
        confirmText="OK"
        cancelText={null}
        type="warning"
      />

      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        onConfirm={() => setShowErrorModal(false)}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText={null}
        type="danger"
      />
    </AnimatePresence>
  );
}

export default UnassignQuizModal;

